import "instantsearch.css/themes/satellite.css";
import "./App.css";
import {
  InstantSearch,
  SearchBox,
  Configure,
  Hits,
  Pagination,
  RefinementList,
  RangeInput,
  useSearchBox,
} from "react-instantsearch";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Icon,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  Box,
  Text,
} from "@chakra-ui/react";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { useGetOrderMediaConfig } from "../views/PlaceOrderTest/queryHooks";
import { useEffect, useState, useMemo } from "react";
import AddPartQuantityModal from "../views/Orders/components/AddPartQuantityModal";
import { isAxiosError } from "axios";

function splitTextForSearchHighlight(text: string, rawQuery: string) {
  const t = text ?? "";
  const q = (rawQuery ?? "").trim();
  if (!q) return [{ text: t, highlight: false }];
  if (!t) return [{ text: "", highlight: false }];

  const tL = t.toLowerCase();
  const qL = q.toLowerCase();

  if (tL.length >= qL.length && tL.endsWith(qL)) {
    const i = t.length - q.length;
    return [
      ...(i > 0 ? [{ text: t.slice(0, i), highlight: false }] : []),
      { text: t.slice(i), highlight: true },
    ];
  }
  if (tL.startsWith(qL)) {
    return [
      { text: t.slice(0, q.length), highlight: true },
      { text: t.slice(q.length), highlight: false },
    ];
  }
  if (tL.includes(qL)) {
    const out: Array<{ text: string; highlight: boolean }> = [];
    let pos = 0;
    let idx = tL.indexOf(qL, pos);
    while (idx !== -1) {
      if (idx > pos) out.push({ text: t.slice(pos, idx), highlight: false });
      out.push({ text: t.slice(idx, idx + q.length), highlight: true });
      pos = idx + q.length;
      idx = tL.indexOf(qL, pos);
    }
    if (pos < t.length) out.push({ text: t.slice(pos), highlight: false });
    return out;
  }

  // Ordered subsequence fallback.
  const marks = new Array<boolean>(t.length).fill(false);
  let si = 0;
  let ri = 0;
  while (si < q.length && ri < t.length) {
    if (q.charAt(si).toLowerCase() === t.charAt(ri).toLowerCase()) {
      marks[ri] = true;
      si++;
    }
    ri++;
  }
  const out: Array<{ text: string; highlight: boolean }> = [];
  let i = 0;
  while (i < t.length) {
    const h = marks[i];
    let j = i + 1;
    while (j < t.length && marks[j] === h) j++;
    out.push({ text: t.slice(i, j), highlight: h });
    i = j;
  }
  return out;
}

function HighlightedSearchText({ text, query }: { text?: string; query: string }) {
  const segments = splitTextForSearchHighlight(text ?? "", query);
  return (
    <>
      {segments.map((s, i) =>
        s.highlight ? (
          <Box as="mark" key={i} bg="yellow.200" display="inline" px={0} lineHeight="inherit">
            {s.text}
          </Box>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

function normalizePartSearchKey(s: string): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]+/g, "");
}

function isPartNumberCompatibleWithSearch(rawQuery: string, partNumber: string, partName?: string): boolean {
  const qRaw = String(rawQuery ?? "").trim();
  if (!qRaw) return true;
  const q = normalizePartSearchKey(qRaw);
  if (!q) return true;
  const pnCompact = normalizePartSearchKey(String(partNumber ?? ""));
  const nameCompact = normalizePartSearchKey(String(partName ?? ""));
  const nameRaw = String(partName ?? "").toLowerCase();
  if (pnCompact.includes(q)) return true;
  if (nameCompact.includes(q)) return true;
  if (nameRaw.includes(qRaw.toLowerCase())) return true;
  return false;
}

function SearchQuerySync({ onQueryChange }: { onQueryChange: (q: string) => void }) {
  const { query } = useSearchBox();
  // Keep local `searchQuery` (used for highlighting + mismatch) synced to InstantSearch query.
  // No UI changes — SearchBox keeps its original appearance (including the cross icon).
  useEffect(() => {
    onQueryChange(query ?? "");
  }, [query, onQueryChange]);
  return null;
}
const FacetedPartSearch = ({
  onClose,
  isOpen,
  orderItems,
  onAddPart,
  remove_idFromPartDetails,
}: any) => {
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const toast = useToast();

  const [selectedPart, setSelectedPart] = useState<any | null>(null);
  const [isQuantityModalOpen, setIsQuantityModalOpen] = useState(false);
  const [quantity, setQuantity] = useState<string>("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [partSearchMismatch, setPartSearchMismatch] = useState<any | null>(null);

  const typesenseInstantsearchAdapter = useMemo(() => {
    return new TypesenseInstantSearchAdapter({
      server: {
        nodes: [
          {
            host: mobileConfigs?.typesense_host,
            port: 443,
            protocol: "https",
          },
        ],
        apiKey: mobileConfigs?.typesense_api_key,
      },
      additionalSearchParameters: {
        query_by: "partName,partNumber",
        // Helps middle-of-part-number matches when the index supports infix.
        infix: "fallback",
      },
    });
  }, [mobileConfigs]);

  const handleConfirmAdd = async () => {
    if (!selectedPart || !onAddPart) return;

    const quantityNum = parseInt(quantity);
    if (isNaN(quantityNum) || quantityNum < 1) {
      toast({
        description: "Quantity must be at least 1",
        status: "error",
        position: "top",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await onAddPart(selectedPart, quantityNum);

      toast({
        description: "Part added successfully",
        status: "success",
        position: "top",
      });

      setIsQuantityModalOpen(false);
      setSelectedPart(null);
      setQuantity("1");
    } catch (error: any) {
      let errorMessage =
        "Failed to add part. Please try again.";
      if (isAxiosError(error)) {
        errorMessage =
          error.response?.data?.statusCode === 409
            ? error.response?.data?.errorMsg
            : errorMessage;
      }
      toast({
        description: errorMessage,
        status: "error",
        position: "top",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const Hit = ({ hit }: any) => {
    const alreadyExists = orderItems?.some(
      (item: any) => item.partId === (hit._id || hit.partId),
    );

    return (
      <Box
        className="hit"
        onClick={() => {
          if (!alreadyExists) {
            const pn = String(hit?.partNumber ?? "").trim();
            const name = String(hit?.partName ?? hit?.name ?? "").trim();
            const q = (searchQuery ?? "").trim();
            const mismatch = q
              ? !isPartNumberCompatibleWithSearch(q, pn, name)
              : false;
            setPartSearchMismatch(
              mismatch
                ? {
                    search: q,
                    selectedPartNumber: pn,
                    selectedPartName: name,
                  }
                : null,
            );
            setSelectedPart(hit);
            setQuantity("1");
            setIsQuantityModalOpen(true);
          }
        }}
        cursor={alreadyExists ? "not-allowed" : "pointer"}
        position="relative"
        padding="12px"
        borderRadius="8px"
        transition="all 0.2s"
        _hover={!alreadyExists ? { backgroundColor: "#F0F6F6" } : {}}
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <div className="hit-content">
          <Text fontSize="sm">
            <HighlightedSearchText text={hit.partName} query={searchQuery} />
          </Text>
          <Text fontSize="sm" fontFamily="mono">
            <HighlightedSearchText text={hit.partNumber} query={searchQuery} />
          </Text>
          <Text fontSize="sm">{hit.brand}</Text>
          <Text fontSize="sm" color="blue.600">
            ₹{hit.mrp}
          </Text>
        </div>
        {alreadyExists && (
          <Text
            color="red.500"
            fontSize="sm"
            fontWeight="bold"
            position="absolute"
            right={2}
            top={2}
          >
            Already Exists
          </Text>
        )}
      </Box>
    );
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"full"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader display={"flex"} justifyContent={"space-between"}>
          Part Search
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <div>
            <InstantSearch
              indexName="partmasters"
              searchClient={typesenseInstantsearchAdapter.searchClient}
            >
              <Configure ruleContexts={[]} />
              <div className="container-1">
                <div className="facets">
                  <div className="facet">
                    <h2>Brands</h2>
                    <RefinementList
                      attribute="brand"
                      searchable={true}
                      searchablePlaceholder="Search brands"
                      showMore={true}
                    ></RefinementList>
                  </div>

                  <div className="facet">
                    <h2>Origin</h2>
                    <RefinementList attribute="origin"></RefinementList>
                  </div>
                </div>

                <div className="search-results">
                  {" "}
                  <SearchBox autoFocus />
                  <SearchQuerySync onQueryChange={setSearchQuery} />
                  <Configure hitsPerPage={20} />
                  <Hits hitComponent={Hit} />
                  <Pagination className="Pagination" />
                </div>
              </div>
            </InstantSearch>
          </div>
        </ModalBody>

        <ModalFooter></ModalFooter>
      </ModalContent>
      <AddPartQuantityModal
        isOpen={isQuantityModalOpen}
        onClose={() => {
          setIsQuantityModalOpen(false);
          setPartSearchMismatch(null);
        }}
        selectedPart={selectedPart}
        quantity={quantity}
        onQuantityChange={setQuantity}
        onConfirm={handleConfirmAdd}
        remove_idFromPartDetails={remove_idFromPartDetails}
        isLoading={isSubmitting}
        partSearchMismatch={partSearchMismatch}
      />
    </Modal>
  );
};

export default FacetedPartSearch;
